import { HttpError, api } from '@/api/client'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/cn'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { createRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { CheckCircle2, Eye, EyeOff, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Route as rootRoute } from './__root'

const schema = z
  .object({
    first_name: z.string().trim().max(50).optional().or(z.literal('')),
    last_name: z.string().trim().max(50).optional().or(z.literal('')),
    password: z.string().min(12, { message: 'min12' }).max(200),
    confirm: z.string().min(1),
  })
  .refine((v) => v.password === v.confirm, { path: ['confirm'], message: 'mismatch' })

type FormValues = z.infer<typeof schema>

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invite',
  validateSearch: (search): { token?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: AcceptInvitePage,
})

function AcceptInvitePage() {
  const { t } = useTranslation(['auth', 'common'])
  const navigate = useNavigate()
  const { token } = useSearch({ from: '/accept-invite' })
  const [showPassword, setShowPassword] = useState(false)
  const [done, setDone] = useState(false)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { first_name: '', last_name: '', password: '', confirm: '' },
  })

  const mutation = useMutation({
    mutationFn: (body: FormValues) =>
      api.auth.acceptInvite({
        token: token ?? '',
        password: body.password,
        first_name: body.first_name || undefined,
        last_name: body.last_name || undefined,
      }),
    onSuccess: () => setDone(true),
  })

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await mutation.mutateAsync(values)
    } catch (err) {
      if (err instanceof HttpError) {
        if (err.status === 401 || err.status === 403 || err.status === 404) {
          form.setError('root', { message: t('auth:invite_invalid') })
        } else {
          form.setError('root', { message: err.message || t('common:error_generic') })
        }
      } else {
        form.setError('root', { message: t('common:error_network') })
      }
    }
  })

  if (!token) {
    return (
      <Shell>
        <Alert variant="danger" title={t('auth:invite_invalid_title')}>
          {t('auth:invite_invalid')}
        </Alert>
      </Shell>
    )
  }

  if (done) {
    return (
      <Shell>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="flex flex-col items-center gap-4 text-center"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-foreground">
            <CheckCircle2 className="h-6 w-6" />
          </div>
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-semibold tracking-tight text-foreground">
              {t('auth:invite_success_title')}
            </h1>
            <p className="text-sm text-muted-foreground">{t('auth:invite_success_body')}</p>
          </div>
          <Button onClick={() => navigate({ to: '/auth/login' })} className="w-full">
            {t('auth:go_to_login')}
          </Button>
        </motion.div>
      </Shell>
    )
  }

  return (
    <Shell>
      <div className="flex flex-col gap-2">
        <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-muted text-foreground">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          {t('auth:invite_title')}
        </h1>
        <p className="text-sm text-muted-foreground">{t('auth:invite_subtitle')}</p>
      </div>

      <form onSubmit={onSubmit} noValidate className="mt-6 flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="first_name">{t('auth:first_name')}</Label>
            <Input id="first_name" autoComplete="given-name" {...form.register('first_name')} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="last_name">{t('auth:last_name')}</Label>
            <Input id="last_name" autoComplete="family-name" {...form.register('last_name')} />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="password">{t('auth:password')}</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              className="pr-10"
              {...form.register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? t('auth:hide_password') : t('auth:show_password')}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {form.formState.errors.password && (
            <p className="text-xs text-red-600 dark:text-red-400">
              {form.formState.errors.password.message === 'min12'
                ? t('auth:password_min12')
                : t('common:error_generic')}
            </p>
          )}
          <p className="text-xs text-muted-foreground">{t('auth:password_hint')}</p>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="confirm">{t('auth:confirm_password')}</Label>
          <Input
            id="confirm"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            {...form.register('confirm')}
          />
          {form.formState.errors.confirm && (
            <p className="text-xs text-red-600 dark:text-red-400">{t('auth:password_mismatch')}</p>
          )}
        </div>

        {form.formState.errors.root && (
          <Alert variant="danger">{form.formState.errors.root.message}</Alert>
        )}

        <Button
          type="submit"
          className="mt-2 w-full"
          disabled={form.formState.isSubmitting || mutation.isPending}
        >
          {mutation.isPending ? t('common:loading') : t('auth:invite_submit')}
        </Button>
      </form>
    </Shell>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="flex min-h-dvh items-center justify-center bg-muted px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className={cn(
          'w-full max-w-md rounded-2xl border border-border bg-background p-8 shadow-sm',
          'dark:border-border',
        )}
      >
        {children}
      </motion.div>
    </main>
  )
}
